import { StatusEnum } from '../../enums/statusEnum';
import { ConsultationStatusEnum } from '../../enums/consultationStatusEnum';

// ContentType enum matching the Java DocumentContentType enum
export const ContentType = {
  DOCUMENT_DETAILS_STATUS: "DOCUMENT_DETAILS_STATUS",
  NHS_ID_STATUS: "NHS_ID_STATUS",
  OWNING_PATIENT_STATUS: "OWNING_PATIENT_STATUS",
  SUMMARY_STATUS: "SUMMARY_STATUS",
  BRIEF_SUMMARY_STATUS: "BRIEF_SUMMARY_STATUS",
  EXISTING_CODES_STATUS: "EXISTING_CODES_STATUS",
  OUTCOME_STATUS: "OUTCOME_STATUS",
  NEW_CODES_STATUS: "NEW_CODES_STATUS",
  POTENTIAL_EXISTING_CODES_STATUS: "POTENTIAL_EXISTING_CODES_STATUS",
  FREQUENT_CODES_STATUS: "FREQUENT_CODES_STATUS",
  MEASUREMENTS_STATUS: "MEASUREMENTS_STATUS",
  LMC_CONTRACT_VIOLATION_STATUS: "LMC_CONTRACT_VIOLATION_STATUS",
  LETTER_TYPE_STATUS: "LETTER_TYPE_STATUS",
  CLINICAL_DEPARTMENT_STATUS: "CLINICAL_DEPARTMENT_STATUS",
  RISK_CATEGORY_STATUS: "RISK_CATEGORY_STATUS"
};

// ConsultationContentType enum matching the Java ConsultationContentType enum
export const ConsultationContentType = {
  OWNING_PATIENT_STATUS: "OWNING_PATIENT_STATUS",
  TRANSCRIPT_STATUS: "TRANSCRIPT_STATUS",
  TRANSCRIPT_SPEAKERS_STATUS: "TRANSCRIPT_SPEAKERS_STATUS",
  NOTES_STATUS: "NOTES_STATUS",
  SUMMARY_STATUS: "SUMMARY_STATUS",
  EXISTING_CODES_STATUS: "EXISTING_CODES_STATUS",
  NEW_CODES_STATUS: "NEW_CODES_STATUS",
  POTENTIAL_EXISTING_CODES_STATUS: "POTENTIAL_EXISTING_CODES_STATUS",
  MEASUREMENTS_STATUS: "MEASUREMENTS_STATUS",
  OUTCOME_STATUS: "OUTCOME_STATUS"
};

// Extra generated types that have no tile of their own, surfaced on a parent tile
export const TILE_RELATED_CONTENT_TYPES = {};

// Helper function to determine if content should be shown
export const shouldShowContent = (field, documentStatus) => {
  return field !== undefined || documentStatus === StatusEnum.READY.value;
};

export const resolveTileErrorContentTypes = (contentType, errorContentTypes) => {
  if (errorContentTypes?.length) {
    return errorContentTypes;
  }
  return TILE_RELATED_CONTENT_TYPES[contentType] || [contentType];
};

// Visible generation error for a tile. Hidden while still
// RECORDING/PENDING/GENERATING (keep spinner / current content) and when filed away.
export const getVisibleTileError = (doc, contentTypes) => {
  if (!doc?.contentTypeToError) {
    return null;
  }
  const status = doc.status;
  if (
    status === StatusEnum.GENERATING.value ||
    status === StatusEnum.PENDING.value ||
    status === StatusEnum.FILED_AWAY.value ||
    status === ConsultationStatusEnum.RECORDING.value
  ) {
    return null;
  }
  const types = (Array.isArray(contentTypes) ? contentTypes : [contentTypes]).filter(Boolean);
  const failedTypes = types.filter((type) => doc.contentTypeToError[type]);
  if (failedTypes.length === 0) {
    return null;
  }
  return {
    contentTypes: failedTypes,
    message: doc.contentTypeToError[failedTypes[0]]?.message || null
  };
};

// Expected owning-patient skips — keep in contentTypeToError, but do not replace the tile body.
// Must match DocumentProcessorBuilder / ConsultationProcessorBuilder onOwningPatient reasons.
export const EXPECTED_OWNING_PATIENT_ERRORS = new Set([
  'patient not found',
  'nhs id is null'
]);

// Expected existing-codes skip — keep in contentTypeToError, but do not replace the tile body.
// Must match BaseExistingCodeService "patient codes data is empty".
export const EXPECTED_EXISTING_CODES_ERRORS = new Set([
  'patient codes data is empty'
]);

export const existingCodesEmptyMessage = (entity) => {
  if (!entity?.patientId) {
    return 'No patient matched';
  }
  const message = entity.contentTypeToError?.[ContentType.EXISTING_CODES_STATUS]?.message;
  if (EXPECTED_EXISTING_CODES_ERRORS.has(message)) {
    return 'Patient had no pre-existing codes';
  }
  return 'Patient had no relevant pre-existing codes';
};

export const formatContentType = (contentType) => {
  if (!contentType) {
    return 'Content';
  }
  return contentType
    .replace('_STATUS', '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\bNhs\b/i, 'NHS')
    .replace(/\bLmc\b/i, 'LMC');
};
